
import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { calculateEndDate, getNextWorkingDay } from "@/components/projects/utils/dateUtils";
import { format, addDays } from "date-fns";
import { toast } from "@/components/ui/use-toast";
import { convertTasksToJson } from "@/components/projects/types";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { TaskFormFields } from "./components/TaskFormFields";
import { TaskDependencySelector } from "./components/TaskDependencySelector";
import { useTaskForm } from "./hooks/useTaskForm";

interface TaskEditDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTask: any;
  setSelectedTask: (task: any) => void;
  selectedProject: any;
  isAddingNewTask: boolean;
  onTaskUpdated: () => void;
  isRestrictedUser?: boolean;
}

const TaskEditDialog = ({ 
  isOpen, 
  onOpenChange, 
  selectedTask, 
  setSelectedTask, 
  selectedProject,
  isAddingNewTask,
  onTaskUpdated,
  isRestrictedUser = false
}: TaskEditDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  const {
    calendarOpen,
    setCalendarOpen,
    calendarEndOpen,
    setCalendarEndOpen,
    availableTasks,
    selectedDependencies,
    handleChange,
    handleDependencyChange
  } = useTaskForm(selectedProject, selectedTask, setSelectedTask);
  
  // Save changes
  const handleSaveChanges = async () => {
    if (!selectedProject || !selectedTask) return;

    // Snapshot ORIGINAL tasks (deep copy of name+dates) BEFORE any mutation so we
    // can compute preserved calendar-day gaps and per-task date deltas.
    const originalTasksSnapshot = (selectedProject.tasks || []).map((t: any) => ({
      name: t?.name,
      startDate: t?.startDate,
      endDate: t?.endDate,
    }));
    const originalTask = originalTasksSnapshot[selectedTask.index] || {};
    
    try {
      setIsLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Error",
          description: "User not authenticated",
          variant: "destructive"
        });
        return;
      }
      
      const updatedTasks = [...selectedProject.tasks];
      const excludeWeekends = selectedProject.excludeWeekends;

      const parseDateSafe = (s?: string): Date | null => {
        if (!s) return null;
        const d = new Date(s);
        return isNaN(d.getTime()) ? null : d;
      };
      const calendarDaysBetween = (a: Date, b: Date): number => {
        const MS = 1000 * 60 * 60 * 24;
        return Math.round((b.getTime() - a.getTime()) / MS);
      };
      
      if (selectedTask.index >= 0) {
        updatedTasks[selectedTask.index] = {
          name: selectedTask.name,
          startDate: selectedTask.startDate,
          endDate: selectedTask.endDate,
          duration: parseInt(selectedTask.duration) || 1,
          progress: parseInt(selectedTask.progress) || 0,
          phase: selectedTask.phase || "Main Tasks",
          dependencies: selectedDependencies.join(','),
          isCritical: selectedTask.isCritical || false,
          isMilestone: false,
        };
        
        // Cascading recalculation for all subsequent tasks — PRESERVE the original
        // calendar-day gap between each task's start date and the previous task's
        // start date. This maintains the transitive day-difference relationship
        // instead of forcing subsequent tasks to start right after the edited task ends.
        for (let i = selectedTask.index + 1; i < updatedTasks.length; i++) {
          const origPrevStart = parseDateSafe(originalTasksSnapshot[i - 1]?.startDate);
          const origCurrStart = parseDateSafe(originalTasksSnapshot[i]?.startDate);
          const newPrevStart = parseDateSafe(updatedTasks[i - 1]?.startDate);
          if (!origPrevStart || !origCurrStart || !newPrevStart) continue;

          const gapDays = calendarDaysBetween(origPrevStart, origCurrStart);
          const nextStart = addDays(newPrevStart, gapDays);
          const nextStartStr = format(nextStart, 'dd MMM yyyy');
          const taskDuration = parseInt(updatedTasks[i].duration) || 1;
          const nextEndStr = calculateEndDate(nextStartStr, taskDuration, excludeWeekends);
          
          updatedTasks[i] = {
            ...updatedTasks[i],
            startDate: nextStartStr,
            endDate: nextEndStr,
          };
        }
      }
      
      // Build the update payload
      const updatePayload: any = {
        project_tasks: convertTasksToJson(updatedTasks)
      };

      // If the first task's start date was changed, sync the project start date
      if (selectedTask.index === 0 && updatedTasks.length > 0 && updatedTasks[0].startDate) {
        const firstTaskStartDate = updatedTasks[0].startDate;
        try {
          const parsed = new Date(firstTaskStartDate);
          if (!isNaN(parsed.getTime())) {
            updatePayload.start_date = format(parsed, 'yyyy-MM-dd');
          }
        } catch (e) {
          console.error('Error parsing first task start date:', e);
        }
      }

      const { error } = await supabase
        .from('projects')
        .update(updatePayload)
        .eq('id', selectedProject.id);
        
      if (error) {
        console.error('Error saving task changes:', error);
        toast({
          title: "Error",
          description: "Failed to save task changes",
          variant: "destructive"
        });
        return;
      }
      
      // Cascade date changes to subtasks for every task whose start date shifted.
      // Each subtask is shifted by the SAME calendar-day delta as its parent task,
      // which preserves both the gap between the subtask and the task start AND
      // the original subtask duration (end_date - start_date).
      try {
        const toIso = (d: Date) => format(d, 'yyyy-MM-dd');
        for (let i = 0; i < updatedTasks.length; i++) {
          const origStart = parseDateSafe(originalTasksSnapshot[i]?.startDate);
          const newStart = parseDateSafe(updatedTasks[i]?.startDate);
          if (!origStart || !newStart) continue;
          const deltaDays = calendarDaysBetween(origStart, newStart);
          if (deltaDays === 0) continue;

          const lookupName = originalTasksSnapshot[i]?.name || updatedTasks[i]?.name;
          if (!lookupName) continue;

          const { data: ptRows } = await supabase
            .from('project_tasks')
            .select('id')
            .eq('project_id', selectedProject.id)
            .eq('name', lookupName);
          const taskIds = (ptRows || []).map((r: any) => r.id);
          if (taskIds.length === 0) continue;

          const { data: subRows } = await supabase
            .from('project_subtasks')
            .select('id, start_date, end_date')
            .eq('project_id', selectedProject.id)
            .in('task_id', taskIds);

          for (const sub of subRows || []) {
            const subStart = parseDateSafe((sub as any).start_date);
            const subEnd = parseDateSafe((sub as any).end_date);
            const upd: any = {};
            if (subStart) upd.start_date = toIso(addDays(subStart, deltaDays));
            if (subEnd) upd.end_date = toIso(addDays(subEnd, deltaDays));
            if (Object.keys(upd).length > 0) {
              const { error: subErr } = await supabase
                .from('project_subtasks')
                .update(upd)
                .eq('id', (sub as any).id);
              if (subErr) console.error('Error shifting subtask dates:', subErr);
            }
          }
        }
      } catch (cascadeErr) {
        console.error('Cascade subtask dates failed:', cascadeErr);
      }

      toast({
        title: "Success",
        description: `Task "${selectedTask.name}" saved successfully`
      });

      onTaskUpdated();
      onOpenChange(false);
      
    } catch (error) {
      console.error("Error saving task:", error);
      toast({
        title: "Error",
        description: "Failed to save task: " + (error as Error).message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };
  
  const handleDelete = async () => {
    if (!selectedProject || !selectedTask || selectedTask.index < 0) return;
    
    try {
      setIsLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Error",
          description: "User not authenticated",
          variant: "destructive"
        });
        return;
      }
      
      const updatedTasks = [...selectedProject.tasks];
      updatedTasks.splice(selectedTask.index, 1);
      
      const { error } = await supabase
        .from('projects')
        .update({
          project_tasks: convertTasksToJson(updatedTasks)
        })
        .eq('id', selectedProject.id);
        
      if (error) {
        console.error('Error deleting task:', error);
        toast({
          title: "Error",
          description: "Failed to delete task",
          variant: "destructive"
        });
        return;
      }
      
      toast({
        title: "Success",
        description: `Task "${selectedTask.name}" deleted successfully`
      });
      
      onTaskUpdated();
      setIsDeleteDialogOpen(false);
      onOpenChange(false);
      
    } catch (error) {
      console.error("Error deleting task:", error);
      toast({
        title: "Error",
        description: "Failed to delete task"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!selectedTask) return null;
  
  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="bg-white text-black max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {isAddingNewTask ? "Add New Task" : "Edit Task"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-2 py-3">
            <TaskFormFields
              selectedTask={selectedTask}
              handleChange={handleChange}
              selectedProject={selectedProject}
              calendarOpen={calendarOpen}
              setCalendarOpen={setCalendarOpen}
              calendarEndOpen={calendarEndOpen}
              setCalendarEndOpen={setCalendarEndOpen}
              isRestrictedUser={isRestrictedUser}
            />

            <TaskDependencySelector
              selectedDependencies={selectedDependencies}
              availableTasks={availableTasks}
              handleDependencyChange={handleDependencyChange}
              isRestrictedUser={isRestrictedUser}
            />
          </div>

          <DialogFooter className="flex justify-end items-center">
            <div className="flex space-x-2">
              {!isAddingNewTask && (
                <Button
                  variant="destructive"
                  onClick={handleDeleteClick}
                  disabled={isLoading || isRestrictedUser}
                  className={`${
                    isRestrictedUser 
                      ? "bg-gray-600 text-gray-400 cursor-not-allowed hover:bg-gray-600" 
                      : "bg-red-600 text-white"
                  }`}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Task
                </Button>
              )}
              
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="bg-white text-black border-gray-300 w-[150px]"
              >
                Cancel
              </Button>

              <Button 
                variant="default"
                onClick={handleSaveChanges}
                disabled={isLoading || isRestrictedUser}
                className={`${
                  isRestrictedUser 
                    ? "bg-gray-600 text-gray-400 cursor-not-allowed hover:bg-gray-600" 
                    : "bg-primary text-primary-foreground"
                }`}
              >
                Save Changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-black">Confirm Delete Task</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600">
              Are you sure you want to delete the task "{selectedTask.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-black border-gray-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 text-white">
              Delete Task
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TaskEditDialog;
